import { type FC, useCallback } from 'react';
import { useFragment, graphql } from 'react-relay';
import PhotoArrows from 'dibs-interiors-photos/exports/PhotoArrows.tsx';
import PhotoPositionIndicator from 'dibs-interiors-photos/exports/PhotoPositionIndicator.tsx';
import PhotoDetailsWrapper from '../PhotoDetailsWrapper.tsx';
import FirmDetailsWrapper from '../FirmDetailsWrapper.tsx';
import PhotoContainer from 'dibs-interiors-photos/exports/PhotoContainer.tsx';
import PhotoContainerWrapper from 'dibs-interiors-photos/exports/PhotoContainerWrapper.tsx';
import PhotoWrapper from 'dibs-interiors-photos/exports/PhotoWrapper.tsx';
import PhotoDetailsSection from 'dibs-interiors-photos/exports/PhotoDetailsSection.tsx';
import PhotoModalCloseButton from 'dibs-interiors-photos/exports/PhotoModalCloseButton.tsx';
import SocialIconsWrapper from '../../sharedComponents/interiors/SocialIconsWrapper.tsx';
import { trackSavePhoto } from 'dibs-interiors-photos/exports/trackingHelpers.ts';
import { useHandleArrowKeyPress } from 'dibs-interiors-photos/exports/useHandleArrowKeyPress.ts';

import { type Photo } from 'dibs-interiors-photos/exports/types.ts';
import { type PhotoContent_photoNode$key } from './__generated__/PhotoContent_photoNode.graphql.ts';

type Props = {
    isModal: boolean;
    onPageChange: (pageUrl: string) => void;
    onCloseModal?: () => void;
    photos: Photo[];
    photoNode: PhotoContent_photoNode$key | null | undefined;
};

const PhotoContent: FC<Props> = props => {
    const { photos, onPageChange, photoNode: photoNodeRef, onCloseModal, isModal } = props;

    const photoNode = useFragment(
        graphql`
            fragment PhotoContent_photoNode on TradePhotosQueryOutput {
                ...PhotoDetailsWrapper_photoNode
                ...FirmDetailsWrapper_photoNode
                firmName
                projectName
                projectUrl
                photo {
                    id
                    path
                    height
                    width
                    serviceId
                    name
                    url
                    styles {
                        name
                    }
                    room {
                        name
                    }
                    photoLike {
                        photoLikeId(userId: $userId) @include(if: $hasUserId)
                        totalPhotoLikes
                    }
                }
            }
        `,
        photoNodeRef
    );

    const photo = photoNode?.photo;
    // when loading the photo modal, use the photo's URL (which will be static across project publishes)
    // instead of the photo's serviceId (which will change on each publish) to determine the user's position
    // within the project photos
    const photoUrl = photo?.url;
    const photoIndex = photos.findIndex(_photo => _photo?.url === photoUrl);
    const photoCount = photos.length;
    const photoLike = photo?.photoLike;

    const onNextClick = useCallback(() => {
        const { url } = photos[photoIndex + 1] || photos[0] || {};
        onPageChange(url || '');
    }, [onPageChange, photos, photoIndex]);

    const onPrevClick = useCallback(() => {
        const { url } = photos[photoIndex - 1] || photos[photos.length - 1] || {};
        onPageChange(url || '');
    }, [onPageChange, photos, photoIndex]);

    useHandleArrowKeyPress({
        onLeftPress: onPrevClick,
        onRightPress: onNextClick,
    });

    return (
        <>
            <PhotoWrapper>
                <PhotoContainerWrapper>
                    {photo && <PhotoContainer shouldShowImage photo={photo} />}
                    <SocialIconsWrapper
                        additionalSpacing={isModal}
                        photoNodeId={photo?.id || ''}
                        photoId={photo?.serviceId || ''}
                        photoLikeId={photoLike?.photoLikeId || ''}
                        totalPhotoLikes={photoLike?.totalPhotoLikes || 0}
                        onPhotoLikeAdded={() => trackSavePhoto({ label: 'photo' })}
                        firmName={photoNode?.firmName || ''}
                        projectName={photoNode?.projectName || ''}
                        projectUrl={photoNode?.projectUrl || ''}
                        photoUrl={photo?.url || ''}
                        photoPath={photo?.path || ''}
                    />
                    <PhotoPositionIndicator photoIndex={photoIndex} photoCount={photoCount} />
                </PhotoContainerWrapper>
                {isModal && onCloseModal && <PhotoModalCloseButton onClick={onCloseModal} />}
                {photoCount > 1 && (
                    <PhotoArrows onNextClick={onNextClick} onPrevClick={onPrevClick} />
                )}
            </PhotoWrapper>
            <PhotoDetailsSection>
                <PhotoDetailsWrapper photoNode={photoNode} />
                <FirmDetailsWrapper photoNode={photoNode} />
            </PhotoDetailsSection>
        </>
    );
};

export default PhotoContent;
